import { type CaptureFileJSONObject, IkeFieldType } from '@ikegps/core-api';
import type { CollectionModelField } from '../collection.field';
import { type MidSpanRoleIndexes, resolveMidSpanRoleIndexes } from '../midspan/midspan.roles';
import { registerFieldType } from './field.registry';
import { CollectionModelFieldTrueSize } from './true.size.field';

/**
 * The dedicated MidSpan tool (UFO-1082): an IKE photo of the span paired with a ground reference shot taken
 * from the same position. Office derives a locked base marker on the span photo from the ground shot, so the
 * crew no longer has to place a height stick in frame.
 *
 * Storage and capture ownership are deliberately identical to {@link CollectionModelFieldTrueSize}: every
 * stored capture — a migrated field's whole photo list, the ground shot included — stays visible, exported,
 * counted and user-markable. Roles only decide where the AUTOMATIC marks go: derivation writes the locked
 * base and wire marker to {@link spanCapture} alone, from the {@link groundCapture} pair. The measured height
 * lives in a sibling HEIGHT field, not here — SpidaCalc and DDS read midspan height by field id off a HEIGHT
 * field (see core-model-pole `wire.span.mapper` / `span.guy.mapper`), so a self-contained height would
 * silently drop out of both exports.
 */
export class CollectionModelFieldMidSpan extends CollectionModelFieldTrueSize {
	static override TYPE: IkeFieldType = IkeFieldType.MID_SPAN;

	static isMidSpanField(f: CollectionModelField): f is CollectionModelFieldMidSpan {
		return f.schema.type === CollectionModelFieldMidSpan.TYPE;
	}

	/**
	 * The photo of the span — the capture the map dot anchors to and the wire measurement lands on.
	 *
	 * Null while captures are still resolving, or when no capture claims the role (both shots mistagged the
	 * same way — see {@link resolveMidSpanRoleIndexes}). A null costs a dot and a derived base, nothing more:
	 * the field owns all of its captures, so visibility and exports never depend on role resolution.
	 */
	get spanCapture(): CaptureFileJSONObject | null {
		const { captures, spanIndex } = this.resolveRoles();
		return spanIndex == null ? null : (captures[spanIndex] ?? null);
	}

	/**
	 * The ground reference shot the locked base is derived from. Null for fields that predate the tool — a
	 * migrated Mid Span IKE Photo carries no role tags — in which case no base is derived.
	 */
	get groundCapture(): CaptureFileJSONObject | null {
		const { captures, groundIndex } = this.resolveRoles();
		return groundIndex == null ? null : (captures[groundIndex] ?? null);
	}

	/**
	 * One capture walk shared by both role getters. Positional fallbacks are meaningless until every stored
	 * id has resolved: mid-load, `getCaptures()` silently drops what has not arrived, so index 0 can be the
	 * ground shot. Per-invocation on purpose — caching across reads would break the MobX fetch registration
	 * that lazy capture loading relies on.
	 */
	private resolveRoles(): MidSpanRoleIndexes & { captures: CaptureFileJSONObject[] } {
		const captures = this.getCaptures();
		const indexes = resolveMidSpanRoleIndexes(
			captures.map((capture) => capture.raw?.role),
			{ allowPositionalFallback: captures.length === this.getCaptureIds().length }
		);
		return { captures, ...indexes };
	}
}

registerFieldType(IkeFieldType.MID_SPAN, (parent, schema) => new CollectionModelFieldMidSpan(parent, schema));
